import { type IDraggableTreeNode } from './draggableTreeTypes.ts';

/**
 * Callback di validazione del drop.
 * Se non fornito, `applyDropToTree` rifiuta drop dentro nodi foglia.
 */
export type CanDropFn<TData> = (
  dragNode: IDraggableTreeNode<TData>,
  dropNode: IDraggableTreeNode<TData>,
  dropToGap: boolean,
) => boolean;

export interface IApplyDropOptions<TData = unknown> {
  treeData: IDraggableTreeNode<TData>[];
  dragKey: string;
  dropKey: string;
  dropToGap: boolean;
  dropPosition: number;
  canDrop?: CanDropFn<TData>;
}

/**
 * Applica un'operazione di drop sull'albero e restituisce il nuovo tree.
 * Restituisce `null` se il drop non è valido.
 */
function applyDropInside<TData>(
  tree: IDraggableTreeNode<TData>[],
  dragNode: IDraggableTreeNode<TData>,
  dropKey: string,
  canDrop?: CanDropFn<TData>,
): IDraggableTreeNode<TData>[] | null {
  const targetNode = findDraggableTreeNode(tree, dropKey);
  if (!targetNode) return null;

  if (canDrop) {
    if (!canDrop(dragNode, targetNode, false)) return null;
  } else if (targetNode.isLeaf) return null;

  targetNode.isLeaf = false;
  targetNode.children ??= [];
  targetNode.children.push(dragNode);
  return tree;
}

function applyDropAtGap<TData>(options: {
  tree: IDraggableTreeNode<TData>[];
  dragNode: IDraggableTreeNode<TData>;
  dropKey: string;
  dropPosition: number;
  canDrop?: CanDropFn<TData>;
}): IDraggableTreeNode<TData>[] | null {
  const { tree, dragNode, dropKey, dropPosition, canDrop } = options;
  if (canDrop) {
    const targetNode = findDraggableTreeNode(tree, dropKey);
    if (!targetNode) return null;
    if (!canDrop(dragNode, targetNode, true)) return null;
  }

  const inserted = insertDraggableTreeNodeAtGap(
    tree,
    dropKey,
    dropPosition,
    dragNode,
  );
  return inserted ? tree : null;
}

/**
 * Applica un'operazione di drop sull'albero e restituisce il nuovo tree.
 * Restituisce `null` se il drop non è valido.
 */
export function applyDropToTree<TData>(
  options: IApplyDropOptions<TData>,
): IDraggableTreeNode<TData>[] | null {
  const { treeData, dragKey, dropKey, dropToGap, dropPosition, canDrop } =
    options;
  const tree = cloneDraggableTree(treeData);

  const dragNode = removeDraggableTreeNode(tree, dragKey);
  if (!dragNode) return null;

  if (!dropToGap) {
    return applyDropInside(tree, dragNode, dropKey, canDrop);
  }

  return applyDropAtGap({ tree, dragNode, dropKey, dropPosition, canDrop });
}

/**
 * Crea una copia profonda della struttura dell'albero (nodi e `children`).
 * Le proprietà non strutturali (es. `data`) mantengono i riferimenti originali.
 */
export function cloneDraggableTree<TData>(
  tree: IDraggableTreeNode<TData>[],
): IDraggableTreeNode<TData>[] {
  return tree.map(node => ({
    ...node,
    children: node.children ? cloneDraggableTree(node.children) : undefined,
  }));
}

/**
 * Rimuove il nodo con la chiave indicata dall'albero (in-place).
 * Restituisce il nodo rimosso, oppure `null` se non trovato.
 */
export function removeDraggableTreeNode<TData>(
  tree: IDraggableTreeNode<TData>[],
  key: string,
): IDraggableTreeNode<TData> | null {
  for (let i = 0; i < tree.length; i++) {
    if (tree[i].key === key) {
      return tree.splice(i, 1)[0];
    }
    if (tree[i].children) {
      const found = removeDraggableTreeNode(tree[i].children!, key);
      if (found) return found;
    }
  }
  return null;
}

/**
 * Trova un nodo per chiave nell'albero.
 */
export function findDraggableTreeNode<TData>(
  tree: IDraggableTreeNode<TData>[],
  key: string,
): IDraggableTreeNode<TData> | null {
  for (const node of tree) {
    if (node.key === key) return node;
    if (node.children) {
      const found = findDraggableTreeNode(node.children, key);
      if (found) return found;
    }
  }
  return null;
}

/**
 * Inserisce un nodo in un gap (tra nodi) nell'albero (in-place).
 */
export function insertDraggableTreeNodeAtGap<TData>(
  tree: IDraggableTreeNode<TData>[],
  dropKey: string,
  dropPosition: number,
  dragNode: IDraggableTreeNode<TData>,
): boolean {
  for (let i = 0; i < tree.length; i++) {
    if (tree[i].key === dropKey) {
      const insertIndex = dropPosition === -1 ? i : i + 1;
      tree.splice(insertIndex, 0, dragNode);
      return true;
    }
    if (tree[i].children) {
      const inserted = insertDraggableTreeNodeAtGap(
        tree[i].children!,
        dropKey,
        dropPosition,
        dragNode,
      );
      if (inserted) return true;
    }
  }
  return false;
}
